import { defineAsyncComponent } from 'vue'
import { FindFilter, VirtualModel } from '../../../types'
import { UserConditionsFilterConditionGroup } from '../../UserConditionsFilter/models/userConditionsFilterColumnsPartial'

const FilterComponentBasedOnTargetModel = {
  template:
    '<FilterResultDisplayContainer :emitValueAsObject="true" v-bind="attrsMerged"/>',
  computed: {
    collectionName() {
      // @ts-ignore
      return this.$attrs.recordRoot?.targetModelName
    },
    attrsMerged() {
      return {
        // @ts-ignore
        ...this.$attrs,
        collectionName: this.collectionName,
      }
    },
  },
}

const templateHelpDocComponent = {
  props: ['record'],
  template: `<HelpDoc class="mb-1">
    <h4 class="font-bold mb-2">
      変数埋め込みを利用して レコードの値やユーザ情報・URLなどを 文面に含めることが可能です。
      <a class="small" href="#" @click.prevent="() => $refs.templateHelpDocModal.show()">
        文面例を見る
      </a>
    </h4>

    <div class="mb-4">
      <div class="d-flex flex-wrap _gap-1">
        <div class="bold">利用可能な埋め込み変数：</div>
        <div class="border px-1 mr-1 bg-white"><code>record</code>: 現在のデータ</div>
        <div class="border px-1 mr-1 bg-white"><code>beforeUpdate</code>: 更新前のデータ</div>
        <div class="border px-1 mr-1 bg-white"><code>user</code>: 通知対象ユーザーデータ</div>
        <div class="border px-1 mr-1 bg-white"><code>records</code>: 全ての対象データの配列</div>
        <div class="border px-1 mr-1 bg-white"><code>recordCount</code>: 対象データの数</div>
        <div class="border px-1 mr-1 bg-white"><code>baseUrl</code>: アプリケーションのURL (例: https://app-stg.example.com )</div>
      </div>
      <div>
        <strong>文面の切替:</strong> アプリケーション内通知と 外部向け (メールなど) の通知文面を 切り替えたい場合は タグ <code v-pre>&lt;onlyExternal&gt;※ メール通知でのみ表示される文面&lt;/onlyExternal&gt;</code> および <code v-pre>&lt;onlyInternal&gt;※アプリ内部でのみ表示される文面&lt;/onlyInternal&gt;</code> を 使用してください。
      </div>
    </div>

    <b-modal size="lg" ref="templateHelpDocModal" title="通知テンプレート文面例">

    <HelpDoc class="text-sm text-gray-600 mb-4">
      <div class="bold mb-1">Tips:</div>
      <ul class="list-disc _pl-4 mb-1" v-pre>
        <li>変数の内容確認: <code>{{json records}}</code> でオブジェクトの中身を確認できます</li>
        <li>条件分岐: <code>{{#if condition}}...{{/if}}</code></li>
        <li>繰り返し: <code>{{#records}}...{{/records}}</code></li>
      </ul>
    </HelpDoc>
    <div class="_mb-4">
      <div class="bold mb-1">使用例1: 注文ステータス変更通知（レコード毎）</div>
      <pre style="font-size: 12px; white-space: pre-wrap;" class="mt-1 border p-1 bg-secondary-light rounded-md" v-pre>
【注文状態変更】注文番号: {{record.orderNumber}}

{{user.first_name}}様

注文番号「{{record.orderNumber}}」の状態が変更されました。

変更内容:
{{#if record.status}}
ステータス: {{beforeUpdate.status}} → {{record.status}}
{{/if}}
{{#if record.deliveryDate}}
納期予定: {{beforeUpdate.deliveryDate}} → {{record.deliveryDate}}
{{/if}}

注文詳細:
- 商品名: {{record.productName}}
- 数量: {{record.quantity}}個
- 金額: {{record.amount}}円

ご確認をお願いいたします。
      </pre>
    </div>

    <div class="mb-4">
      <div class="bold mb-1">使用例2: 日次注文サマリー（まとめて通知）</div>
      <pre style="font-size: 12px; white-space: pre-wrap;" class="mt-1 border p-1 bg-secondary-light  rounded-md" v-pre>
【本日の注文サマリー】{{recordCount}}件の更新

{{user.first_name}}様

本日更新された注文は以下の通りです：

{{#records}}
■ 注文番号: {{orderNumber}}
  商品名: {{productName}}
  状態: {{status}}
  {{#if _beforeUpdate}}
  変更点:
    {{#if _beforeUpdate.status}}
    - ステータス: {{_beforeUpdate.status}} → {{status}}
    {{/if}}
    {{#if _beforeUpdate.deliveryDate}}
    - 納期予定: {{_beforeUpdate.deliveryDate}} → {{deliveryDate}}
    {{/if}}
  {{/if}}
{{/records}}

合計金額: {{#sum records "amount"}}{{/sum}}円
      </pre>
    </div>

    <div class="mb-4">
      <div class="bold mb-1">使用例3: 緊急注文アラート（レコード毎）</div>
      <pre style="font-size: 12px; white-space: pre-wrap;" class="mt-1 border p-1 bg-secondary-light rounded-md" v-pre>
【緊急】特急注文受付 - {{record.orderNumber}}

{{user.first_name}}様

特急注文を受け付けました。
至急対応をお願いいたします。

■ 注文詳細
注文番号: {{record.orderNumber}}
顧客名: {{record.customerName}}
希望納期: {{record.requestedDeliveryDate}}
商品名: {{record.productName}}
数量: {{record.quantity}}個

備考:
{{record.notes}}

対応状況の更新をお願いいたします。
      </pre>
    </div>

    <div class="mb-4">
      <div class="bold mb-1">使用例4: 在庫不足アラート（まとめて通知）</div>
      <pre style="font-size: 12px; white-space: pre-wrap;" class="mt-1 border p-1 bg-secondary-light rounded-md" v-pre>
【警告】在庫不足商品 - {{recordCount}}件

{{user.first_name}}様

以下の商品で在庫不足が発生しています：

{{#records}}
■ 商品: {{productName}}
  現在庫数: {{stockQuantity}}
  発注点: {{reorderPoint}}
  未出荷注文数: {{backorderCount}}件

  関連する注文:
  {{#pendingOrders}}
  - 注文番号: {{orderNumber}} ({{quantity}}個)
  {{/pendingOrders}}

{{/records}}

至急、在庫補充の検討をお願いいたします。
      </pre>
    </div>

    <div class="mb-4">
      <div class="bold mb-1">使用例5: 承認依頼通知（レコード毎）</div>
      <pre style="font-size: 12px; white-space: pre-wrap;" class="mt-1 border p-1 bg-secondary-light rounded-md" v-pre>
【承認依頼】特別価格注文 - {{record.orderNumber}}

{{user.first_name}}様

以下の注文について承認依頼が届いています。

■ 注文情報
注文番号: {{record.orderNumber}}
顧客名: {{record.customerName}}
商品名: {{record.productName}}
数量: {{record.quantity}}個

■ 価格情報
定価: {{record.listPrice}}円
特別価格: {{record.specialPrice}}円
値引率: {{record.discountRate}}%

■ 承認理由
{{record.approvalReason}}

承認または却下の対応をお願いいたします。
      </pre>
    </div>

    <div class="mb-4">
      <div class="bold mb-1">高度な使用例：条件分岐とフォーマット</div>
      <pre style="font-size: 12px; white-space: pre-wrap;" class="mt-1 border p-1 bg-secondary-light rounded-md" v-pre>
【注文レポート】{{formatDate now "YYYY年MM月DD日"}}

{{user.first_name}}様

{{#if (gt recordCount 10)}}
※ 大量の更新があります。ご注意ください。
{{/if}}

■ 集計情報
対象期間: {{formatDate startDate "YYYY/MM/DD"}} 〜 {{formatDate endDate "YYYY/MM/DD"}}
更新件数: {{recordCount}}件
合計金額: {{formatNumber totalAmount}}円

■ ステータス別件数
{{#groupBy records "status"}}
- {{@key}}: {{this.length}}件
{{/groupBy}}

■ 重要度の高い注文
{{#records}}
{{#if (eq priority "high")}}
- {{orderNumber}}: {{customerName}} ({{formatDate deliveryDate "MM/DD"}})
{{/if}}
{{/records}}

■ 金額帯別集計
{{#groupBy records (groupByAmount amount)}}
{{@key}}: {{this.length}}件
{{/groupBy}}

詳細は管理画面でご確認ください。
      </pre>
    </div>
    </b-modal>
  </HelpDoc>`,
}

const triggerTypes = {
  afterSave: 'レコード作成/更新時',
  afterDelete: 'レコード削除時',
  schedule: 'スケジュール実行',
  // eventHook: 'イベントフック実行時',
}

export const virtualModel__core_server_side_action_definitions: VirtualModel = {
  baseModel: 'core_server_side_flows',
  name: 'core_server_side_action_definitions',
  tableLabel: 'サーバーサイドアクション定義',
  dataFilters: {
    _and: [
      {
        definitionType: { _eq: 'core_server_side_action_definition' },
      },
    ],
  },
  defaultValues: async () => {
    return {
      // name: 'サーバーサイドアクション定義',
      // description: 'サーバーサイドアクション定義',
      definitionType: 'core_server_side_action_definition',
      // targetModelName: 'complaints',
      // triggerType: 'schedule',
      scheduleSettings: [
        {
          scheduleType: 'weekly',
          intervalValue: 1,
          intervalUnit: 'weeks',
          daysOfWeek: ['MON', 'WED'],
        },
      ],
      actionSettings: [
        {
          actionType: 'notification',
          notificationSettings: [
            {
              // createNotificationPerRecord: true,
              titleTemplate: '${record.name}が更新されました',
              bodyTemplate: `<onlyExternal>{{ user.first_name }} さん

お世話になっております。</onlyExternal>
{{records.0.id}} が更新されました。`,
              categoryName: 'record_update',
              priority: 'normal',
              channels: ['email'],
            },
          ],
        },
      ],
    }
  },
  formColumnGroups: {
    // basic: {
    //   type: 'tab',
    //   groupKey: 'tab1',
    //   label: '基本設定',
    //   order: 10,
    // },
    trigger: {
      type: 'tab',
      groupKey: 'tab1',
      label: 'トリガー設定',
      icon: 'sign-out-alt',
      order: 20,
    },
    conditions: {
      type: 'tab',
      groupKey: 'tab1',
      icon: 'filter',
      label: '対象レコード抽出条件',
      order: 30,
    },
    userConditions: {
      type: 'tab',
      groupKey: 'tab1',
      icon: 'user',
      label: '対象ユーザー条件',
      order: 40,
    },
    actions: {
      type: 'tab',
      groupKey: 'tab1',
      label: 'アクション設定',
      order: 50,
    },
  },

  columns: {
    // 基本情報
    id: {
      type: 'UUID',
      label: 'ID',
      visible: false,
    },
    name: {
      type: 'STRING',
      label: 'アクション定義名',
      validate: { notEmpty: true },
      width: { xs: 48, sm: 20 },
      groupKey: 'basic',
      listItemAttrs: {
        class: {
          'no-ellipsis': true,
          'whitespace-nowrap': true,
        },
        style: { minWidth: '320px' },
      },
    },
    category: {
      type: 'STRING',
      label: 'カテゴリ',
      width: { xs: 48, sm: 20 },
      groupKey: 'basic',
      // visible: false,
    },
    definitionType: {
      type: 'STRING',
      label: '定義タイプ',
      visible: false,
    },
    description: {
      type: 'TEXT',
      label: '説明',
      width: { xs: 48, sm: 28 },
      groupKey: 'basic',
      inputHelpText: 'アクションの目的や用途について説明を記載',
    },
    enabled: {
      type: 'BOOLEAN',
      label: '有効状態',
      defaultValue: true,
      width: { xs: 'max-content' },
      groupKey: 'basic',
    },
    targetModelName: {
      virtualColumnOf: 'otherData',
      type: 'STRING',
      label: '対象モデル',
      validate: { notEmpty: true },
      strictSelections: true,
      editable: true,
      editableOnCreate: true,
      groupKey: 'basic',
      selections: () => $core.$modelsLoader.modelNames,
      customLabel: (val) =>
        $core.$models[val]?.tableLabel
          ? `${$core.$models[val].tableLabel} (${val})`
          : val,
      width: { xs: 'max-content' },
    },

    // トリガー設定
    triggerType: {
      virtualColumnOf: 'otherData',
      type: 'SELECT',
      inputComponent: 'RadioSelectInput',
      label: 'トリガータイプ',
      groupKey: 'trigger',
      validate: { notEmpty: true },
      selections: () => Object.keys(triggerTypes),
      customLabel: (val) => triggerTypes[val],
      width: { xs: 'max-content' },
      // afterComponents: [
      //   {
      //     template: `スケジュール実行 をテストする場合には `,
      //   },
      // ],
    },

    // スケジュール設定
    scheduleSettings: {
      virtualColumnOf: 'otherData',
      type: 'ARRAY_OF_OBJECT',
      visibleOnIndex: false,
      label: '実行スケジュール設定',
      inputHelpText: '複数の条件を OR 条件で設定可能です。',
      groupKey: 'trigger',
      enableIf: (row) => row.triggerType === 'schedule',
      width: { xs: 48 },
      beforeComponents: [
        {
          template: '<DebugKickScheduleExecution />',
          components: {
            DebugKickScheduleExecution: defineAsyncComponent(
              // @ts-ignore
              () => import('./DebugKickScheduleExecution.vue'),
            ),
          },
        },
      ],
      columns: {
        baseDate: {
          type: 'DATEONLY',
          label: '基準日',
          defaultValue: () => $core.$dayjs().format('YYYY-MM-DD'),
          inputAttrs: { wrapperStyle: 'max-width: 180px' },
          width: { xs: 'max-content' },
        },
        intervalValue: {
          type: 'NUMBER',
          label: '間隔',
          validate: { notEmpty: true, min: 1 },
          inputAttrs: { wrapperStyle: 'max-width: 100px' },
          defaultValue: 1,
        },
        scheduleType: {
          type: 'SELECT',
          label: '期間',
          validate: { notEmpty: true },
          selections: () => [
            { value: 'daily', label: '日毎' },
            { value: 'weekly', label: '週毎' },
            { value: 'monthly', label: 'ヶ月毎' },
          ],
          inputAttrs: { wrapperStyle: 'max-width: 120px' },
          width: { xs: 'max-content' },
          defaultValue: 'weekly',
        },
        monthlyDateSpecifyType: {
          type: 'SELECT',
          label: '月内の指定方法',
          enableIf: (row) => row.scheduleType === 'monthly',
          validate: { notEmpty: true },
          width: { xs: 'max-content' },
          selections: () => [
            { value: 'dates', label: '月内日付指定' },
            { value: 'weeks', label: '月内週番号指定' },
          ],
          defaultValue: 'dates',
        },
        weeksOfMonth: {
          type: 'MULTISELECT',
          label: '週番号',
          enableIf: (row) =>
            row.scheduleType === 'monthly' && row.monthlyDateSpecifyType === 'weeks',
          validate: { notEmpty: true },
          selections: () => [1, 2, 3, 4, 5],
          customLabel: (val) => `第${val}週`,
          width: { xs: 'max-content' },
          defaultValue: [1],
        },
        daysOfWeek: {
          type: 'MULTISELECT',
          // inputComponent: 'MultiselectCheckboxesInput',
          label: '曜日指定',
          enableIf: (row) =>
            row.scheduleType === 'weekly' ||
            (row.scheduleType === 'monthly' && row.monthlyDateSpecifyType === 'weeks'),
          validate: { notEmpty: true },
          selections: () => [
            { value: 'MON', label: '月' },
            { value: 'TUE', label: '火' },
            { value: 'WED', label: '水' },
            { value: 'THU', label: '木' },
            { value: 'FRI', label: '金' },
            { value: 'SAT', label: '土' },
            { value: 'SUN', label: '日' },
          ],
          width: { xs: 'max-content' },
        },
        daysOfMonth: {
          type: 'MULTISELECT',
          label: '月内日付指定',
          enableIf: (row) =>
            row.scheduleType === 'monthly' && row.monthlyDateSpecifyType === 'dates',
          validate: { notEmpty: true },
          selections: () => {
            return [...Array.from({ length: 30 }, (_, i) => i + 1), 'lastDay']
          },
          customLabel: (val) => {
            return val === 'lastDay' ? '月末' : `${val}日`
          },
          width: { xs: 'max-content' },
          defaultValue: 'lastDay',
        },
        timeOfDay: {
          type: 'STRING',
          label: '実行時刻',
          validate: { notEmpty: true },
          width: { xs: 'max-content' },
          selections: () => generateTimeSelections(),
          defaultValue: '10:00',
        },
      },
    },

    // イベントフック設定
    eventHookSettings: {
      visibleOnIndex: false,
      virtualColumnOf: 'otherData',
      type: 'ARRAY_OF_OBJECT',
      label: 'イベントフック設定',
      groupKey: 'trigger',
      enableIf: (row) => row.triggerType === 'eventHook',
      width: { xs: 48 },
      columns: {
        eventName: {
          type: 'STRING',
          label: 'イベント名',
          validate: { notEmpty: true },
          width: { xs: 'max-content' },
        },
        //         paramValidations: {
        //           type: 'ARRAY_OF_OBJECT',
        //           label: 'パラメータバリデーション',
        //           width: { xs: 48 },
        //           columns: {
        //             paramName: {
        //               type: 'STRING',
        //               label: 'パラメータ名',
        //               validate: { notEmpty: true },
        //               width: { xs: 'max-content' },
        //             },
        //             paramType: {
        //               type: 'SELECT',
        //               label: '型',
        //               validate: { notEmpty: true },
        //               selections: () => [
        //                 { value: 'string', label: '文字列' },
        //                 { value: 'number', label: '数値' },
        //                 { value: 'boolean', label: '真偽値' },
        //                 { value: 'object', label: 'オブジェクト' },
        //                 { value: 'array', label: '配列' },
        //               ],
        //               width: { xs: 'max-content' },
        //             },
        //             required: {
        //               type: 'BOOLEAN',
        //               label: '必須',
        //               defaultValue: true,
        //               width: { xs: 'max-content' },
        //             },
        //             validationFunction: {
        //               type: 'TEXT',
        //               label: 'バリデーション関数',
        //               inputComponent: 'CodeEditor',
        //               width: { xs: 48 },
        //               inputAttrs: {
        //                 initCode: `/**
        //  * @param {any} value - パラメータの値
        //  * @returns {Promise<boolean>} - バリデーション結果
        //  */
        // return async (value) => {
        //   // バリデーション処理を記述
        //   return true
        // }`,
        //               },
        //             },
        //           },
        //         },
      },
    },

    // アクション設定
    actionSettings: {
      virtualColumnOf: 'otherData',
      type: 'ARRAY_OF_OBJECT',
      visibleOnIndex: false,
      label: 'アクション設定',
      groupKey: 'actions',
      width: { xs: 48 },
      columns: {
        actionType: {
          type: 'SELECT',
          label: 'アクションタイプ',
          validate: { notEmpty: true },
          selections: () => [
            { value: 'notification', label: '通知' },
            { value: 'recordUpdate', label: 'レコード更新' },
            { value: 'functionExecution', label: '関数実行' },
          ],
          width: { xs: 'max-content' },
        },

        // 通知設定
        notificationSettings: {
          type: 'ARRAY_OF_OBJECT',
          label: '通知設定',
          enableIf: (row) => row.actionType === 'notification',
          width: { xs: 48 },
          minValueLength: 1,
          maxValueLength: 1,
          columns: {
            // TODO: これは できなくなったので 削除
            // createNotificationPerRecord: {
            //   type: 'BOOLEAN',
            //   label: 'レコード毎に通知を作成',
            //   defaultValue: true,
            //   width: { xs: 48 },
            //   inputHelpText:
            //     'ONの場合は各レコードに対して個別の通知を作成します。OFFの場合は複数レコードを1つの通知にまとめます。',
            // },
            categoryName: {
              type: 'STRING',
              label: 'カテゴリ名',
              validate: { notEmpty: true },
              inputHelpText: '通知一覧でグルーピングされるカテゴリの表示名を設定します。',
              width: { xs: 'max-content' },
            },
            titleTemplate: {
              type: 'TEXT',
              label: 'タイトルテンプレート',
              inputComponent: 'CInputString',
              width: { xs: 48 },
              validate: { notEmpty: true },
              beforeComponents: [templateHelpDocComponent],
            },
            bodyTemplate: {
              type: 'TEXT',
              label: '本文テンプレート',
              validate: { notEmpty: true },
              width: { xs: 48 },
              inputAttrs: { rows: 10 },
            },
            priority: {
              visible: false,
              type: 'SELECT',
              label: '優先度',
              defaultValue: 'normal',
              selections: () => [
                { value: 'low', label: '低' },
                { value: 'normal', label: '通常' },
                { value: 'high', label: '高' },
              ],
            },
            channels: {
              visible: false,
              type: 'MULTISELECT',
              label: '通知チャンネル',
              validate: { notEmpty: true },
              selections: () => [
                { value: 'email', label: 'メール' },
                { value: 'teams', label: 'Microsoft Teams' },
                // その他追加可能なチャンネル
              ],
            },
          },
        },

        // レコード更新設定
        recordUpdateSettings: {
          type: 'ARRAY_OF_OBJECT',
          label: 'レコード更新設定',
          enableIf: (row) => row.actionType === 'recordUpdate',
          width: { xs: 48 },
          columns: {
            targetModelName: {
              type: 'STRING',
              label: '対象モデル',
              validate: { notEmpty: true },
              strictSelections: true,
              selections: () => $core.$modelsLoader.modelNames,
              customLabel: (val) =>
                $core.$models[val]?.tableLabel
                  ? `${$core.$models[val].tableLabel} (${val})`
                  : val,
            },
            updateFields: {
              type: 'ARRAY_OF_OBJECT',
              label: '更新フィールド',
              width: { xs: 48 },
              columns: {
                fieldName: {
                  type: 'STRING',
                  label: 'フィールド名',
                  validate: { notEmpty: true },
                  dynamicSelections: true,
                  selections: (record, currentValue, initialValue, recordRoot) => {
                    const targetModel = record.targetModelName
                    return targetModel ? $core.$models[targetModel].colNames : []
                  },
                },
                value: {
                  type: 'TEXT',
                  label: '設定値',
                  validate: { notEmpty: true },
                  inputHelpText: '変数を利用可能: ${record.fieldName}',
                },
              },
            },
          },
        },

        // 関数実行設定
        functionExecutionSettings: {
          type: 'ARRAY_OF_OBJECT',
          label: '関数実行設定',
          enableIf: (row) => row.actionType === 'functionExecution',
          width: { xs: 48 },
          columns: {
            functionCode: {
              type: 'TEXT',
              label: '実行関数',
              validate: { notEmpty: true },
              inputComponent: 'CodeEditor',
              width: { xs: 48 },
              inputAttrs: {
                initCode: `/**
 * @param {Object} context - 実行コンテキスト
 * @param {Record<string, any>} context.record - 現在のレコード
 * @param {Record<string, any>} context.triggerContext - トリガー情報
 * @param {Object} context.service - サービスインスタンス
 * @returns {Promise<void>}
 */
return async ({ record, triggerContext, service }) => {
  // 実行する処理を記述
}`,
              },
            },
          },
        },

        // アクション実行前のコールバック
        useBeforeExecuteCallback: {
          visible: false,
          type: 'BOOLEAN',
          label: 'アクション実行前の処理を利用',
          defaultValue: false,
          width: { xs: 'max-content' },
        },
        beforeExecuteCallback: {
          visible: false,
          type: 'TEXT',
          label: 'アクション実行前の処理',
          enableIf: (row) => row.useBeforeExecuteCallback,
          inputComponent: 'CodeEditor',
          width: { xs: 48 },
          inputAttrs: {
            initCode: `/**
 * @param {Object} context - 実行コンテキスト
 * @param {Record<string, any>} context.record - 現在のレコード
 * @param {Object} context.action - 実行予定のアクション
 * @param {Object} context.service - サービスインスタンス
 * @returns {Promise<boolean>} - falseを返すとアクションを実行しない
 */
return async ({ record, action, service }) => {
  // 処理を記述
  return true
}`,
          },
        },

        // アクション実行後のコールバック
        useAfterExecuteCallback: {
          type: 'BOOLEAN',
          visible: false,
          label: 'アクション実行後の処理を利用',
          defaultValue: false,
          width: { xs: 'max-content' },
        },
        afterExecuteCallback: {
          type: 'TEXT',
          label: 'アクション実行後の処理',
          enableIf: (row) => row.useAfterExecuteCallback,
          inputComponent: 'CodeEditor',
          width: { xs: 48 },
          inputAttrs: {
            initCode: `/**
 * @param {Object} context - 実行コンテキスト
 * @param {Record<string, any>} context.record - 現在のレコード
 * @param {Object} context.action - 実行されたアクション
 * @param {Object} context.result - アクション実行結果
 * @param {Object} context.service - サービスインスタンス
 * @returns {Promise<void>}
 */
return async ({ record, action, result, service }) => {
  // 処理を記述
}`,
          },
        },

        // アクション完了時のイベント発行設定
        emitEvents: {
          visible: false,
          type: 'ARRAY_OF_OBJECT',
          label: 'イベント発行設定',
          width: { xs: 48 },
          columns: {
            eventName: {
              type: 'STRING',
              label: 'イベント名',
              validate: { notEmpty: true },
            },
            eventParamsFunction: {
              type: 'TEXT',
              label: 'パラメータ生成関数',
              inputComponent: 'CodeEditor',
              width: { xs: 48 },
              inputAttrs: {
                initCode: `/**
 * @param {Object} context - 実行コンテキスト
 * @param {Record<string, any>} context.record - 現在のレコード
 * @param {Object} context.action - 実行されたアクション
 * @param {Object} context.result - アクション実行結果
 * @returns {Promise<Object>} - イベントパラメータ
 */
return async ({ record, action, result }) => {
  return {
    // イベントパラメータを生成
  }
}`,
              },
            },
          },
        },
      },
    },
    beforeSaveRecordFetchFields: {
      type: 'MULTISELECT',
      virtualColumnOf: 'otherData',
      label: '更新前のリレーション先を含めたデータ取得指定',
      groupKey: 'conditions',
      strictSelections: false,
      inputHelpText:
        '更新前のレコードの リレーション先を含めた データ取得カラムを 複数指定します。 例: <code>*.*</code>, <code>product.name</code>, <code>product.id</code>, <code>product.vendors.*</code>',
      width: { xs: 'max-content' },
      enableIf: (row, recordRoot) =>
        recordRoot.triggerType === 'afterSave' &&
        recordRoot.recordFilteringSettings?.some(
          (setting) => setting?.filterUpdatedRecords === true,
        ),
    },
    recordFetchFields: {
      type: 'MULTISELECT',
      virtualColumnOf: 'otherData',
      groupKey: 'conditions',
      label: 'リレーション先を含めたデータ取得指定',
      strictSelections: false,
      inputHelpText:
        '対象レコードの リレーション先を含めた データ取得カラムを 複数指定します。後続の処理 (通知文面の作成など) で リレーション先のデータを 利用する場合は ここで 指定してください。例: <code>*.*</code>, <code>product.name</code>, <code>product.id</code>, <code>product.vendors.*</code>',
      width: { xs: 'max-content' },
      // enableIf: (row, recordRoot) => recordRoot.triggerType === 'afterSave',
    },
    // レコード条件設定
    recordFilteringSettings: {
      virtualColumnOf: 'otherData',
      visibleOnIndex: false,
      type: 'ARRAY_OF_OBJECT',
      label: '対象レコード抽出条件',
      groupKey: 'conditions',
      enableIf: (row, recordRoot) => recordRoot.targetModelName,
      width: { xs: 48 },
      minValueLength: 1,
      inputHelpText: '複数の条件を OR 条件で設定可能です。',
      columns: {
        recordConditions: {
          type: 'JSON',
          label: 'レコード抽出条件',
          inputComponent: FilterComponentBasedOnTargetModel,
          width: { xs: 'max-content' },
          enableIf: (row, recordRoot) =>
            ['afterSave', 'afterDelete'].includes(recordRoot.triggerType) === false,
        },
        filterCreatedRecords: {
          type: 'BOOLEAN',
          label: '新規作成時',
          defaultValue: true,
          width: { xs: 'max-content' },
          enableIf: (row, recordRoot) => recordRoot.triggerType === 'afterSave',
        },
        filterUpdatedRecords: {
          type: 'BOOLEAN',
          label: '更新時',
          defaultValue: true,
          width: { xs: 'max-content' },
          enableIf: (row, recordRoot) => recordRoot.triggerType === 'afterSave',
        },
        // TODO: 実装するかどうか 悩ましい
        // filterUpdatedRecordsOnlyValueChanged: {
        //   type: 'BOOLEAN',
        //   label: '値が変更されたレコードのみ対象とする',
        //   width: { xs: 'max-content' },
        //   enableIf: (row, recordRoot) =>
        //     recordRoot.triggerType === 'afterSave' && row.filterUpdatedRecords,
        //   defaultValue: true,
        // },
        beforeSaveRecordConditions: {
          beforeComponents: [{ template: '<div></div>' }],
          type: 'JSON',
          label: '更新前のレコード抽出条件',
          inputComponent: FilterComponentBasedOnTargetModel,
          width: { xs: 'max-content' },
          enableIf: (row, recordRoot) =>
            recordRoot.triggerType === 'afterSave' && row.filterUpdatedRecords,
          inputHelpText: '条件を指定しない場合は、すべてのレコードが対象となります。',
        },
        savedRecordConditions: {
          beforeComponents: [{ template: '<div></div>' }],
          type: 'JSON',
          label: '保存後のレコード抽出条件',
          inputComponent: FilterComponentBasedOnTargetModel,
          width: { xs: 'max-content' },
          enableIf: (row, recordRoot) => recordRoot.triggerType === 'afterSave',
          inputHelpText: '条件を指定しない場合は、すべてのレコードが対象となります。',
        },
        beforeDeleteRecordConditions: {
          type: 'JSON',
          label: '削除前のレコード抽出条件',
          inputComponent: FilterComponentBasedOnTargetModel,
          width: { xs: 'max-content' },
          enableIf: (row, recordRoot) => recordRoot.triggerType === 'afterDelete',
        },
      },
    },

    // 対象ユーザー条件
    targetUserConditions: {
      virtualColumnOf: 'otherData',
      visibleOnIndex: false,
      type: 'JSON',
      label: '対象ユーザー条件',
      groupKey: 'userConditions',
      inputComponent: {
        template: `<UserConditionsFilterWithModalInput v-bind="binds" />`,
        props: ['recordRoot'],
        computed: {
          binds() {
            return {
              ...this.$attrs,
              targetModelNames: [this.recordRoot.targetModelName],
              isOnServerSideContext: true, // サーバサイドでのフィルタ条件関数の init code 表示のために必要
            }
          },
        },
      },
      width: { xs: 48 },
      inputHelpText: 'アクションの対象となるユーザーの条件を設定します',
    },
  },
}

/**
 * サーバーサイドアクション定義の型定義
 */
export type CoreServerSideActionDefinition = {
  id: string
  name: string
  category: string
  description: string
  definitionType: 'core_server_side_action_definition'
  enabled: boolean
  targetModelName: string
  createdAt: string
  updatedAt: string
  connections: any[]
  nodeConfigs: any[]
  configureables: any[]
  metadata: {
    tags: string[]
    category: string
  }
  otherData: CoreServerSideActionDefinitionBehaviorData
}

export type CoreServerSideActionDefinitionBehaviorData = {
  triggerType: TriggerType
  targetModelName: string
  scheduleSettings: ScheduleSettings[]
  actionSettings: ActionSettings[]
  // 更新前のレコード取得フィールド
  beforeSaveRecordFetchFields: string[]
  // 更新後のレコード取得フィールド
  recordFetchFields: string[]
  // レコード抽出条件
  recordFilteringSettings: CoreServerSideActionDefinitionBehaviorRecordFilteringSetting[]
  // 対象ユーザー条件
  targetUserConditions: UserConditionsFilterConditionGroup[]
}

type TriggerType = 'afterSave' | 'afterDelete' | 'schedule' | 'eventHook'

type ScheduleSettings = {
  baseDate: string
  timeOfDay: string
  daysOfWeek: ('MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN')[]
  daysOfMonth: number | 'lastDay'
  scheduleType: 'daily' | 'weekly' | 'monthly'
  weeksOfMonth: number[]
  intervalValue: number
  monthlyDateSpecifyType: 'dates' | 'weeks'
}

type ActionSettings = {
  actionType: 'notification' | 'recordUpdate' | 'functionExecution'
  emitEvents: any[]
  notificationSettings?: NotificationSetting[]
  useAfterExecuteCallback: boolean
  useBeforeExecuteCallback: boolean
}

export type NotificationSetting = {
  priority: 'low' | 'normal' | 'high'
  titleTemplate: string
  bodyTemplate: string
  categoryName: string
  channels: ('email' | 'teams')[]
  createNotificationPerRecord: boolean | null
}

export type CoreServerSideActionDefinitionBehaviorRecordFilteringSetting = {
  filterCreatedRecords: boolean
  filterUpdatedRecords: boolean
  // TODO: 実装するかどうか 悩ましい
  // filterUpdatedRecordsOnlyValueChanged: boolean
  recordConditions: FindFilter | null
  beforeSaveRecordConditions: FindFilter | null
  savedRecordConditions: FindFilter | null
  beforeDeleteRecordConditions: FindFilter | null
}

const cache = {}
const generateTimeSelections = () => {
  const key = 'timeSelections'
  if (cache[key]) {
    return cache[key]
  }
  const hours = Array.from({ length: 24 }, (_, i) => i)
  const minutes = Array.from({ length: 6 }, (_, i) => i * 10)
  const result = hours.flatMap((hour) =>
    minutes.map((minute) => {
      return `${`${hour}`.padStart(2, '0')}:${`${minute}`.padStart(2, '0')}`
    }),
  )
  cache[key] = result
  return result
}
